import { postWorkFor } from "../api/users";
import { getApiErrorCode } from "../utils/api-error";
import {
  dispatchCompareCostInvalidate,
  dispatchWorkForChanged,
} from "../utils/compare-events";
import { readStoredWorkFor, writeStoredWorkFor } from "../utils/work-for-storage";

// A refusal carries a server code; a network blip or a 500 does not. Only a
// refusal proves the target is unusable, so only a refusal clears it.
const REFUSAL_CODES = new Set([
  "work-for-user-not-found",
  "work-for-not-permitted",
  "work-for-use-user-id",
]);

// One at a time: this runs from the boot/refresh path, which fires twice on a
// cold load (the boot outcome, then the first refresh). `inFlight` only covers
// overlap, so a short floor stops the sequential duplicate as well. It is far
// shorter than any plausible staleness window -- a target revoked mid-session
// is still caught on the next refresh.
const REVALIDATE_FLOOR_MS = 30_000;
let inFlight = false;
let lastValidated: { userId: string; at: number } | null = null;

function fallBackToSelf(): void {
  lastValidated = null;
  writeStoredWorkFor({ kind: "self" });
  dispatchWorkForChanged();
  dispatchCompareCostInvalidate();
}

/**
 * Re-validate the stored Work-For target and write back what the server
 * actually honours.
 *
 * Reads fall back to SELF server-side when a stored target stops being
 * permitted — deactivated, promoted to internal, moved region — and record it
 * only in a log. The banner reads `sessionStorage`, so it went on naming a
 * customer whose data was no longer being served: writes refused loudly, reads
 * did not. A long-lived tab is where that sat unnoticed.
 *
 * Never throws: a transient failure leaves the stored target alone rather than
 * dropping a usable one.
 */
export async function reconcileStoredWorkFor(): Promise<void> {
  const stored = readStoredWorkFor();
  // An unregistered "Other" has no account to go stale this way.
  if (stored.kind !== "registered") return;
  if (inFlight) return;
  if (
    lastValidated?.userId === stored.userId &&
    Date.now() - lastValidated.at < REVALIDATE_FLOOR_MS
  ) {
    return;
  }
  inFlight = true;

  try {
    const { effective } = await postWorkFor({ userId: stored.userId });

    if (effective.kind !== "registered" || !effective.userId) {
      fallBackToSelf();
      return;
    }

    // From the server's `effective`, never the stored copy — the same rule the
    // cross-origin handoff follows.
    const next = {
      kind: "registered" as const,
      userId: effective.userId,
      email: effective.email ?? "",
      name: [effective.firstName, effective.lastName]
        .filter(Boolean)
        .join(" ")
        .trim(),
      company: effective.companyName ?? undefined,
    };
    if (next.userId !== stored.userId || next.email !== stored.email) {
      writeStoredWorkFor(next);
      dispatchWorkForChanged();
      dispatchCompareCostInvalidate();
    }
    lastValidated = { userId: next.userId, at: Date.now() };
  } catch (err) {
    if (REFUSAL_CODES.has(getApiErrorCode(err) ?? "")) fallBackToSelf();
  } finally {
    inFlight = false;
  }
}
